/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/ban-types */
import * as Yup from 'yup';
import * as vp from 'vopay';
import React, { useMemo, useEffect, useState } from 'react';
import { ObjectShape } from 'yup/lib/object';
import { FormikValues, useFormik } from 'formik';
import { FieldData, FormCtx, FormikOptions } from './types';

const { Icon } = vp;

export function getValidationSchema<T>(
  ctx: FormCtx<T>,
  fieldDataList: FieldData<T>[],
  initalValidationSchema?: { [key: string]: any },
): Yup.ObjectSchema<ObjectShape> {
  const validations = fieldDataList
    .filter((fieldData) => fieldData.validate && fieldData.showField(ctx.values))
    .reduce(
      (existingValidations, fieldData) => ({
        ...existingValidations,
        [fieldData.fieldName]: fieldData.validate && fieldData.validate.call(fieldData, ctx),
      }),
      { ...initalValidationSchema },
    );

  return Yup.object().shape(validations);
}

export function getRequiredIcon(): React.JSX.Element {
  return <Icon icon="asterisk" color="error" height="8px" width="8px" />;
}

export function formikHandlerField<T, K, J>(
  ctx: FormCtx<T>,
  fieldData: FieldData<T>,
  fieldOptions: FormikOptions<K, J>,
  completeDataList: FieldData<T>[],
) {
  return {
    onFocus: (event: React.FocusEvent<any>) => {
      // loop though all previous   and set to as touched

      const currentIndex = completeDataList.findIndex((data) => data.fieldName === fieldData.fieldName);

      if (currentIndex !== -1) {
        // Check if the current field was found
        completeDataList.forEach((data, index) => {
          // Set touched for all fields up to the current index
          if (index < currentIndex) {
            ctx.setFieldTouched(data.fieldName, true, true);
          }
        });
      }
      if (hasOnFocus(fieldOptions)) {
        fieldOptions.onFocus(event);
      }
      function hasOnFocus(
        obj: Record<string, any>,
      ): obj is { onFocus: (event: React.FocusEvent<HTMLElement>) => void } {
        return 'onFocus' in obj;
      }
    },
    onBlur: (event: React.FocusEvent<any>) => {
      ctx.handleBlur(event);

      if (hasBlur(fieldOptions)) {
        fieldOptions.onBlur(event);
      }

      function hasBlur(obj: Record<string, any>): obj is { onBlur: (event: React.FocusEvent<HTMLElement>) => void } {
        return 'onBlur' in obj;
      }
    },
    onChange: (event: React.ChangeEvent<any>, data?: any) => {
      ctx.handleChange(event);

      if (fieldOptions.onChange) {
        fieldOptions.onChange(event as any, data);
      }
    },
    name: String(fieldData.fieldName),
    error:
      ctx.errors[fieldData.fieldName] && ctx.touched[fieldData.fieldName]
        ? ctx.errors[fieldData.fieldName]?.toString()
        : null,
    errorMsg:
      ctx.errors[fieldData.fieldName] && ctx.touched[fieldData.fieldName]
        ? ctx.errors[fieldData.fieldName]?.toString()
        : undefined,
    value: fieldOptions.formatValue
      ? fieldOptions.formatValue(ctx.values[fieldData.fieldName] as unknown as J)
      : (ctx.values[fieldData.fieldName] as unknown as K),
  };
}
export function renderFormElementDefault<T>(
  ctx: FormCtx<T>,
  fieldData: FieldData<T>,
  completeDataList: FieldData<T>[],
): React.JSX.Element {
  const { ElementContainer, Box } = vp;

  return (
    <ElementContainer
      key={fieldData.fieldName}
      label={
        <Box ai="center" gapSize={fieldData.getInputState(ctx) === 'required' ? '2px' : '4px'}>
          {fieldData.label(ctx)}
          {fieldData.getInputState(ctx) === 'required' && getRequiredIcon()}
          {fieldData.tooltip && fieldData.tooltip(ctx)}
        </Box>
      }
      padding="0px 0px 8px"
      isBold={false}
      input={
        <Box flx="col">
          {fieldData.renderField &&
            fieldData.renderField({
              ctx,
              getFormikData: (options) => formikHandlerField(ctx, fieldData, options, completeDataList),
            })}
        </Box>
      }
    />
  );
}
type RenderFormElementsParams<T> = {
  ctx: FormCtx<T>;
  renderFieldDataList: FieldData<T>[];
  completeDataList?: FieldData<T>[];
  renderFormElement?: (ctx: FormCtx<T>, fieldData: FieldData<T>) => React.ReactNode;
};
export function renderFormElements<T>({
  ctx,
  renderFieldDataList,
  completeDataList,
  renderFormElement,
}: RenderFormElementsParams<T>): React.ReactNode[] {
  const renderElement = renderFormElement || renderFormElementDefault;
  const DataList = (completeDataList || renderFieldDataList).filter((fieldData) => fieldData.showField(ctx.values));
  return renderFieldDataList
    .filter((fieldData) => fieldData.showField(ctx.values))
    .map((fieldData) => renderElement(ctx, fieldData, DataList));
}

export function serializeForm<T, K extends object>(values: T, fieldDataList: FieldData<T>[]): K {
  return fieldDataList.reduce(
    (serializedForm, fieldData) => ({
      ...serializedForm,
      [fieldData.fieldName]: fieldData.showField(values)
        ? fieldData.serializeField
          ? fieldData.serializeField(values)
          : undefined
        : undefined,
    }),
    {} as K,
  );
}

type FormProps<T extends FormikValues, Serialized extends object> = {
  config: FieldData<T>[];
  initialValues: T;
  onSubmit: (values: Serialized) => void;
};
export function useForm<T extends FormikValues, Serialized extends object>({
  config,
  initialValues,
  onSubmit,
}: FormProps<T, Serialized>) {
  const [validationSchema, setValidationSchema] = useState<Yup.ObjectSchema<ObjectShape>>(Yup.object().shape({}));

  const formik = useFormik({
    validationSchema,
    initialValues,
    onSubmit: (values) => {
      const vals = serializeForm<T, Serialized>(values, config);

      onSubmit(vals);
    },
  });

  const ctx = useMemo(
    () => ({
      values: formik.values,
      errors: formik.errors,
      touched: formik.touched,
      handleBlur: formik.handleBlur,
      handleChange: formik.handleChange,
      setFieldTouched: formik.setFieldTouched,
      setFieldValue: formik.setFieldValue,
      handleSubmit: formik.handleSubmit,
      validateForm: formik.validateForm,
      setValues: formik.setValues,
      isValid: formik.isValid,
    }),
    [formik.values, formik.errors, formik.touched],
  );

  useEffect(() => {
    setValidationSchema(getValidationSchema(ctx, config));
  }, [ctx]);

  useEffect(() => {
    formik.validateForm();
  }, [validationSchema]);

  return {
    ctx,
    renderFormElements: ({ config: subConfig }: { config: FieldData<T>[] }) =>
      renderFormElements({ renderFieldDataList: subConfig, completeDataList: config, ctx }),
  };
}
